/**
 * Phase 1 groundwork for Complimentary Ticket email notifications.
 *
 * Subject: plain text input. Body: shared <RichTextEditor /> (same
 * toolbar as Events description). A separate `TokenBar` above the body
 * inserts three placeholders — {codes}, {province}, {fullname} — at
 * the cursor of whichever field was focused last. Placeholders are
 * substituted per-recipient in Phase 2.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Ticket, Shield, User } from "lucide-react";
import { useApi } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import RichTextEditor from "@/components/RichTextEditor";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TOKENS = [
  {
    tag: "{codes}",
    label: "Codes",
    icon: Ticket,
    description:
      "The complimentary code(s) generated for this recipient. Multiple codes are comma-separated.",
  },
  {
    tag: "{province}",
    label: "Province",
    icon: Shield,
    description:
      "Short Province label linked to the code (e.g. PGL Bedfordshire or PGL Middlesex).",
  },
  {
    tag: "{fullname}",
    label: "Full Name",
    icon: User,
    description:
      "The recipient's Linked To name, including title (e.g. W.Bro John Smith).",
  },
];

export default function AdminComplimentaryEmailTemplatePage() {
  const api = useApi();
  const navigate = useNavigate();
  const { key } = useParams();
  const [tpl, setTpl] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Which field owns the cursor right now — decides where the next
  // token click drops its text. Defaults to the body so a first-click
  // still lands somewhere useful even before the user focuses either
  // input.
  const [focusTarget, setFocusTarget] = useState("body");
  const subjectRef = useRef(null);
  const subjectSel = useRef({ start: 0, end: 0 });
  const editorRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api(`/admin/complimentary-email-templates/${key}`);
        const d = await r.json();
        if (!r.ok) {
          toast.error(d.detail || "Could not load template");
          navigate("/admin");
          return;
        }
        setTpl(d);
        setSubject(d.subject || "");
        setBody(d.body_html || "");
      } finally { setLoading(false); }
    })();
  }, [key, api, navigate]);

  const rememberSubjectSelection = () => {
    const el = subjectRef.current;
    if (!el) return;
    subjectSel.current = {
      start: el.selectionStart ?? subject.length,
      end: el.selectionEnd ?? subject.length,
    };
  };

  const insertToken = (tag) => {
    if (focusTarget === "subject") {
      const { start, end } = subjectSel.current;
      const s = String(subject);
      const next = s.slice(0, start) + tag + s.slice(end);
      setSubject(next);
      // Restore caret just after the inserted tag on next tick.
      setTimeout(() => {
        const el = subjectRef.current;
        if (!el) return;
        el.focus();
        const pos = start + tag.length;
        el.setSelectionRange(pos, pos);
        subjectSel.current = { start: pos, end: pos };
      }, 0);
      return;
    }
    // Body path — hand the token to the editor controller.
    editorRef.current?.insert(tag);
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api(`/admin/complimentary-email-templates/${key}`, {
        method: "PUT",
        body: JSON.stringify({ subject, body_html: body }),
      });
      const d = await r.json();
      if (!r.ok) return toast.error(d.detail || "Save failed");
      setTpl(d);
      toast.success("Template saved");
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading…</div>;
  }

  return (
    <div>
      <PageHeader
        title={`Email template — ${tpl?.label || key}`}
        subtitle="Phase 1: draft the subject and body. Phase 2 will wire the send flow."
        testId="comp-email-tpl-header"
      />
      <Card className="border-2 border-red-500/60 p-5 space-y-4">
        <div className="space-y-1">
          <Label>Subject</Label>
          <Input
            ref={subjectRef}
            data-testid="comp-email-subject"
            placeholder="Complimentary tickets — <event name>"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onFocus={() => { setFocusTarget("subject"); rememberSubjectSelection(); }}
            onSelect={rememberSubjectSelection}
            onKeyUp={rememberSubjectSelection}
            onClick={rememberSubjectSelection}
            maxLength={300}
          />
          <p className="text-xs text-muted-foreground">
            Plain text. Kept as-is; no formatting is applied to the subject line.
          </p>
        </div>

        {/* Token insert bar — sits between Subject and Body so it reads
            as a single control for both fields. Uses lucide icons per
            the design (Ticket / Shield / User). */}
        <div
          className="rounded-md border border-dashed border-red-500/40 bg-red-50/40 p-3"
          data-testid="comp-email-tokens"
        >
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-sm font-semibold text-slate-800">Insert linked data</div>
            <div className="text-[11px] text-muted-foreground">
              Inserts into whichever field the cursor is in.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {TOKENS.map((t) => (
              <button
                key={t.tag}
                type="button"
                onClick={() => insertToken(t.tag)}
                title={`${t.label} (${t.tag}) — ${t.description}`}
                aria-label={`Insert ${t.label} token ${t.tag}`}
                data-testid={`comp-email-token-${t.tag.replace(/[{}]/g, "")}`}
                className="group flex items-start gap-2 max-w-[240px] text-left px-3 py-2 rounded-md border border-slate-200 bg-white hover:border-red-500 hover:bg-red-50 transition-colors"
              >
                <t.icon size={18} className="text-red-600 mt-0.5 shrink-0" />
                <span className="leading-tight">
                  <span className="block text-sm font-semibold text-slate-900">
                    {t.label} <span className="font-mono text-xs text-slate-500">{t.tag}</span>
                  </span>
                  <span className="block text-[11px] text-muted-foreground">
                    {t.description}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1" onFocus={() => setFocusTarget("body")}>
          <Label>Body</Label>
          <RichTextEditor
            value={body}
            onChange={setBody}
            testId="comp-email-body"
            placeholder="Write the email body — H1 headings appear in navy, H2 in red."
            onReady={(ctrl) => { editorRef.current = ctrl; }}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" asChild disabled={busy}>
            <Link to="/admin">Back</Link>
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            data-testid="comp-email-save"
            className="bg-slate-900 text-white hover:bg-red-600 hover:text-white"
          >
            {busy ? "Saving…" : "Save template"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
